<template>
  <div>
    <div class="p-4 font-medium text-xs text-left leading-snug max-h-96 overflow-auto space-y-2 bg-charcoal-100 dark:bg-dark-950">
      <template v-if="!attendance || (attendance.transactions ?? []).length <= 0">
        <div class="bg-white dark:bg-dark-800 dark:text-dark-50 p-4 rounded-xl text-light-950 shadow">
          <p class="text-lg font-bold mb-0.5">{{ $t('components.Event.guests.title') }}</p>
          <p class="text-sm">{{ $t('components.Event.guests.content', { max: event.maxGuests ?? 5 }) }}</p>

          <InputCount v-model="guests" class="mt-4" :max="event.maxGuests ?? 5" />
        </div>
        <div class="pt-2 pb-1">
          <ButtonLink :is-loading="isLoadingCancel" @click="onCancel">
            {{ $t('components.Event.actions.cancel') }}
          </ButtonLink>
        </div>
      </template>
      <div v-else>
        <div class="text-lg font-bold">{{ $t('components.Event.refund.title') }}</div>
        <div class="text-sm mt-2">{{ $t('components.Event.refund.content') }}</div>

        <div class="mt-4 w-full space-y-2">
          <div class="bg-white dark:bg-dark-800 dark:text-dark-50 rounded-md px-4 py-3 shadow flex items-center hover:opacity-100" :class="[ daysBefore >= step.days && daysBefore < (event.refund.steps[i - 1]?.days ?? 9999) ? '' : 'opacity-50']" v-for="(step, i) in event.refund.steps">
            <div class="grow">
              <div class="font-bold text-sm text-royal-900 dark:text-royal-400">
                {{ $t(`components.Event.refund.${event.refundPolicy}.step${i + 1}.title`) }}
              </div>
              <div class="text-base">
                {{ $t(`components.Event.refund.${event.refundPolicy}.step${i + 1}.content`) }}
              </div>
            </div>

            <div class="size-8 bg-royal-200 text-royal-900 rounded-full flex items-center justify-center" v-if="daysBefore >= step.days && daysBefore < (event.refund.steps[i - 1]?.days ?? 9999)">
              <IconBase name="cute/check-fill" :width="14" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="bg-white border-t border-charcoal-200 dark:bg-dark-800 dark:border-dark-600 flex items-center space-x-2 p-2.5">
      <ButtonBase style-type="round" class="shrink-0" color="white" @click="() => emit('previous')">
        <IconBase name="cute/angle-right" class="rotate-180" :width="20" />
      </ButtonBase>

      <ButtonBase color="base" is-full :is-loading="isLoading" @click="onNext" v-if="!attendance || (attendance.transactions ?? []).length <= 0">
        {{ $t('components.Event.actions.update') }}
      </ButtonBase>
      <ButtonBase color="ruby" :timer="3"  is-full :is-loading="isLoadingCancel" @click="onCancel" v-else>
        {{ $t('components.Event.actions.cancel') }}
      </ButtonBase>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Event, EventProduct } from '@/classes'
import EventStore from '@/store/events'
import UserStore from '@/store/user'
import dayjs from 'dayjs'

const props = withDefaults(defineProps<{
  event: Event
  onEnd: () => Promise<boolean>
}>(), {

})

const { event } = toRefs(props)

const eventStore = EventStore()
const userStore = UserStore()
const { currentUser } = storeToRefs(userStore)

const guests = defineModel({
  default: 0
})

const daysBefore = computed(() => {
  if (!event.value.date) return 999

  return dayjs(event.value.date).diff(dayjs(), 'day')
})

const emit = defineEmits<{
  (e: 'update', params: { guests: number }): void
  (e: 'next'): void
  (e: 'previous'): void
  (e: 'reset'): void
}>()

const isLoading = ref(false)

const onNext = async () => {
  isLoading.value = true

  emit('update', { guests: guests.value })

  const result = await props.onEnd()

  if (result) emit('next')

  isLoading.value = false
}

const isLoadingCancel = ref(false)

const attendance = computed(() => event.value.getAttendance(currentUser.value?._id))
const route = useRoute()

const onCancel = async () => {
  if (!currentUser.value || !props.event) return

  isLoadingCancel.value = true
  await eventStore.attendCancel(props.event._id)

  useNuxtApp().$amplitude.logEvent(`Event_Leave`, {
    source: route.fullPath
  })

  emit('reset')

  isLoadingCancel.value = false
}

onMounted(() => {
  guests.value = event.value.attendees.find(u => u._id === currentUser.value?._id)?.guests ?? 0
})

</script>